import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';
import { isChunkLoadError, mainBundleHash, mayAutoReload } from './appUpdateCore';

// Side effects for appUpdateCore.ts (identical in both apps).

const RELOAD_KEY = 'chunk-reload-at';

/**
 * Reload the page once for a failed chunk. Returns false (no reload) when we already reloaded
 * within the guard window or sessionStorage is unavailable — a reload loop is worse than the
 * error screen.
 */
export function reloadOnceForChunkError(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_KEY);
    const now = Date.now();
    if (!mayAutoReload(raw === null ? null : Number(raw), now)) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** React.lazy whose failed chunk reloads the page once (the new index.html names the new chunks). */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(() => factory().catch((err) => {
    if (isChunkLoadError(err) && reloadOnceForChunkError()) {
      return new Promise<{ default: T }>(() => { /* the page is reloading */ });
    }
    throw err;
  }));
}

/** The main bundle hash the server currently serves (index.html is no-cache, ~1.5 KB), or null. */
export async function fetchServedBundleHash(): Promise<string | null> {
  try {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/index.html`, { cache: 'no-store', credentials: 'omit' });
    if (!res.ok) return null;
    return mainBundleHash(await res.text());
  } catch {
    return null;
  }
}

export function anyMediaPlaying(): boolean {
  return Array.from(document.querySelectorAll('audio, video'))
    .some((m) => !(m as HTMLMediaElement).paused);
}

export function anyDialogOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
}
